import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import financialSquare from './../../assets/images/financial-shapes-square.svg';
import financialCircle from './../../assets/images/financial-shapes-circle.svg';
import financialTriangle from './../../assets/images/financial-shapes-triangle.svg';
import './../financial.scss';
import AOS from "aos";
import { gsap } from 'gsap';
import "aos/dist/aos.css";

export default function FinancialPlanning() {

  const squareRef = useRef();
  const triangle1Ref = useRef();
  const triangle2Ref = useRef();

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {

    document.addEventListener('aos:in:bar-anim', function(ev) {
      //console.log('animated in', ev);

      if (ev.detail.querySelectorAll('.progress').length > 1) {
        //console.log("more than 1 progress");
        var barArray = [...ev.detail.querySelectorAll('.progress')];
      } else {
        //console.log("less than 1 progress");
        var barArray = [];
        var bar = ev.detail.querySelector('.progress');
        //console.log(bar);
        barArray.push(bar);
      }

      //console.log(barArray);

      if (barArray && barArray.length) {
        barArray.forEach(function(barItem) {
          var fill = barItem.getAttribute('data-fill');

          if (!ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.width = fill + '%';
          }
        });
      }

      var Cont={val:0};

      if (ev.detail.querySelectorAll('.percentage').length > 1) {
        var percentageArray = [...ev.detail.querySelectorAll('.percentage')];
        //console.log("more than 1 percentage");
      } else {
        var percentageArray = [];
        var percentage = ev.detail.children[0].querySelector('.percentage');
        percentageArray.push(percentage);
        //console.log("less than percentage");

      }

      //console.log(percentage);

      if (percentageArray) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }

      //console.log(percentageArray);

    });

    document.addEventListener('aos:in:percentage', function(ev) {
      var Cont={val:0};
      var percentage = ev.detail.children[0].querySelector('.percentage');
      var percentageArray = [];
      percentageArray.push(percentage);
      //console.log(percentage);

      if (percentage) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }
    });

    // Had to make some adjustments since it seemed like the animation wasn't playing nice with React. The shapes weren't incrementing exactly 90 degrees (it was adding a few degrees with decimal places) so they always rotated crooked. Possibly React strict mode causing issues. Added gsap.context, refs, and using GSAP's ScrollTrigger instead of AOS. 
    let ctx = gsap.context(() => {

      var tl = gsap.timeline({ 
        onComplete:rotateAgain,
        scrollTrigger: {
          trigger: ".shape-images"
        }
      });

      function animate(){
        tl
        .to(squareRef.current, 0.5, {rotation:"+=90"})
        .to(triangle1Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(triangle2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
      }

      function rotateAgain(){
        gsap.delayedCall(5, animate)
      }

      animate();

    });

    return () => ctx.revert(); // <- cleanup!

    AOS.init({
      once: true
    });

    AOS.refresh();

  }, []);

  return (
    <>
      <div className="financial-planning">

        <p id="s1" className="intro" data-aos="fade-up">Discover how today’s financial businesses plan and manage their direct mail marketing efforts.&#8288;<a href="#reference-1" className="cm-article-content__reference" aria-label="Footnote 1" title="Footnote 1"><sup aria-hidden="true">[1]</sup></a></p>
        <div className='two-cols'>
          <div className='col-right'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Direct Mail Expertise</h3>
                <div className='pie-chart' data-aos>
                  <svg viewBox="0 0 32 32" role="img" alt="" title="">
                    <circle r="16" cx="16" cy="16" strokeDasharray="0 100"></circle>
                  </svg>
                </div>

                <span data-aos data-aos-id="percentage" data-aos-anchor=".pie-chart">
                  <span data-aos className='percentage-value'><span class="percentage" data-to="70" aria-hidden="true">0</span><span class="screen-reader-text">70</span>%</span>
                </span>

                <p>Advanced knowledge</p>
                <p>Among all the respondents surveyed, those in financial industries reported having the highest expertise with direct mail.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>Getting started with direct mail doesn’t need to be complicated. To get a better handle on the basics of direct mail, explore a <a href="/dm-101-tutorial/">step-by-step beginner’s guide</a>, covering everything from data and mailing lists to ROI and postage.</p>
              </div>
            </div>

            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>In-House Mail <br />Management</h3>
                <div data-aos data-aos-id="percentage">
                  <span data-aos className='percentage-value large highlighter'><span class="percentage" data-to="58" aria-hidden="true">0</span><span class="screen-reader-text">58</span>%</span>
                </div>
                <p>Performance measurement in-house</p>
                <p><span className='printing-percentage'>49%</span> Printing in-house</p>
                <p>Among all respondents, financial companies are the most likely to do direct mail performance measurement and printing in-house.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>The <a className="external-link" href="https://gateway.usps.com/eAdmin/view/signin" target="_blank">Business Customer Gateway</a> makes it easy to manage all things direct mail in one centralized location—from tracking and monitoring mailings to managing Mailer IDs and permits.</p>
              </div>
            </div>
          </div>

          <div className='col-left'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Management of Direct Mail</h3>
                <p className="pull-quote">Across industries, businesses typically have <span data-aos data-aos-once="true" className='highlighter'>either in-house or agency-shared responsibility</span> for managing various aspects of direct mail. Few companies depend exclusively on outside agencies.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p> <a href="/../questions-to-ask-before-choosing-a-mail-service-provider/">Specialized partners</a> can help you manage some—or all—elements of direct mail campaigns. Mail service providers (MSPs), advertising agencies and printers can help with different needs, including writing and designing your piece, making sure your mail meets USPS requirements, printing and presorting, and more.</p>
              </div>
            </div>

            <div className='personalized-box secondary'>
              <h3 id="s2">The USPS Connect<sup>&trade;</sup> Local mail service&#8288;<a href="#reference-2" className="cm-article-content__reference" aria-label="Footnote 2" title="Footnote 2"><sup aria-hidden="true">[2]</sup></a> can be an ideal solution for financial businesses.</h3>
              <div className='shape-images'>
                <div className='square'>
                  <img ref={squareRef} src={financialSquare} alt="" />
                </div>
                <div className='circle'>
                  <img src={financialCircle} alt="" />
                </div>
                <div className='triangle triangle-1'>
                  <img ref={triangle1Ref} src={financialTriangle} alt="" />
                </div>
                <div className='triangle triangle-2'>
                  <img ref={triangle2Ref} src={financialTriangle} alt="" />
                </div>
              </div>
            </div>

            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Direct Mail and USPS Services</h3>
                <div className='progress-bar-area' data-aos data-aos-id="bar-anim">
                  <div className='progressbar-item'>
                    <span className='label'>USPS Connect Local mail</span>
                    <div className='progress-bar size-small'>
                      <div className='progress' data-fill="51"><span class="percentage" data-to="51" aria-hidden="true">0</span><span class="screen-reader-text">51</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>Informed Delivery<sup>&reg;</sup></span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="50"><span class="percentage" data-to="50" aria-hidden="true">0</span><span class="screen-reader-text">50</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>Scheduled pickup</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="50"><span class="percentage" data-to="50" aria-hidden="true">0</span><span class="screen-reader-text">50</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>Every Door Direct Mail<sup>&reg;</sup></span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="44"><span class="percentage" data-to="44" aria-hidden="true">0</span><span class="screen-reader-text">44</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>Informed Visibility<sup>&reg;</sup></span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="41"><span class="percentage" data-to="41" aria-hidden="true">0</span><span class="screen-reader-text">41</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>None of these</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="5"><span class="percentage" data-to="5" aria-hidden="true">0</span><span class="screen-reader-text">5</span>%</div>
                    </div>
                  </div>
                </div>
                <p>Of several USPS services, financial companies are most likely to use USPS Connect Local mail, a commercial First-Class Mail<sup>&reg;</sup> offering designed to deliver paper-based items same-day or next-day to local customers.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>The <a className="external-link" href="https://www.usps.com/business/connect/" target="_blank">Connect Local mail service</a> can be an ideal and affordable solution for financial businesses that regularly send out time-sensitive documents, ensuring customers get urgent information quickly and efficiently—and that it won’t be missed amid the digital clutter.</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
